import axios, {
  AxiosError,
  AxiosRequestConfig,
  CreateAxiosDefaults,
} from 'axios';

import { getApiUrl } from '@/app/configs/getAppConfig';

const defaultConfig: CreateAxiosDefaults = {
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  baseURL: `${getApiUrl().apiUrl}`,
};

// interceptors устанавливаются в /app/useApplyAppInterceptor.ts
// baseUrl устанавливаются в /app/useApplyApiUrl.ts

const baseInstance = axios.create({
  ...defaultConfig,
});

// Базовый API (gateway)
export const apiInstance = axios.create({
  ...baseInstance.defaults,
});

export const authInstance = axios.create({
  ...baseInstance.defaults,
  baseURL: `${getApiUrl().apiUrl}/api/v1/auth`,
});

export const notificationInstance = axios.create({
  ...baseInstance.defaults,
  baseURL: `${getApiUrl().apiUrl}/api/v1`,
});

export const integrationsInstance = axios.create({
  ...baseInstance.defaults,
});

export const accountInstance = axios.create({
  ...baseInstance.defaults,
});

export const channelManagementInstance = axios.create({
  ...baseInstance.defaults,
});

export const frontOfficeInstance = axios.create({
  ...baseInstance.defaults,
});

export const subscriptionInstance = axios.create({
  ...baseInstance.defaults,
});

// Универсальный маппинг сервисов -> axios-инстансы
type TServiceKey =
  | 'api'
  | 'auth'
  | 'notification'
  | 'integrations'
  | 'account'
  | 'channelManagement'
  | 'frontOffice'
  | 'subscription';

const SERVICE_INSTANCES: Record<TServiceKey, typeof apiInstance> = {
  api: apiInstance,
  auth: authInstance,
  notification: notificationInstance,
  integrations: integrationsInstance,
  account: accountInstance,
  channelManagement: channelManagementInstance,
  frontOffice: frontOfficeInstance,
  subscription: subscriptionInstance,
};

// Универсальная фабрика запроса по ключу сервиса
export const createServiceRequest = <T>(
  service: TServiceKey,
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const instance = SERVICE_INSTANCES[service];
  const source = axios.CancelToken.source();

  const promise = instance({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data as T);

  // @ts-expect-error Adding cancel method to promise for react-query compatibility
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

export const createAuthInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return createServiceRequest<T>('auth', config, options);
};

export const createNotificationInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return createServiceRequest<T>('notification', config, options);
};

export const createIntegrationsInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return createServiceRequest<T>('integrations', config, options);
};

export const createAccountInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return createServiceRequest<T>('account', config, options);
};

export const createSubscriptionsInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return createServiceRequest<T>('subscription', config, options);
};

export const createChannelManagementInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return createServiceRequest<T>('channelManagement', config, options);
};

export const createFrontOfficeInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return createServiceRequest<T>('frontOffice', config, options);
};

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;
